import { useCallback } from "react";
import { Heading, Stack, Text } from "@chakra-ui/react";
import NextImage from "next/image";

import Button from "components/Chakra/Button";
import CustomLink from "components/Chakra/CustomLink";
import useRouter from "hooks/useRouter";
import { EOfferStatus, EOfferTypeId, IMyOffer } from "utils/offer";
import { TActivation } from ".";
import IconEdit from "components/Chakra/Icons/Edit";
import IconStats from "components/Chakra/Icons/Stats";
import CardLayout from "./CardLayout";

type CardOfferType = {
  activationHandler: (id: number, action: TActivation) => void;
  status: EOfferStatus;
  paidTypeId: number;
  offer: IMyOffer;
};

const getOfferType = (offerTypeId: EOfferTypeId | 3) => {
  switch (offerTypeId) {
    case 3:
      return "Prodej";
    case EOfferTypeId.RENT:
      return "Pronájem";
    case EOfferTypeId.COHABITING:
      return "Spolubydlení";
  }
};

const CardOffer: React.FC<CardOfferType> = ({
  activationHandler,
  offer,
  paidTypeId,
  status,
}) => {
  const { navigateTo } = useRouter();

  const calculateTitle = useCallback((offerPreview: IMyOffer) => {
    const str =
      `${getOfferType(offerPreview.offerTypeId)} - ` +
      offerPreview.village.label +
      (offerPreview.street?.label && ` - ${offerPreview.street.label}`);
    if (str.length > 40) {
      return str.slice(0, 37) + "...";
    }

    return str;
  }, []);

  const resolveButton = (): JSX.Element =>
    status === EOfferStatus.EXPIRED ? (
      <Button
        h="32px"
        alignItems="center"
        display="flex"
        w="fit-content"
        variant="outline"
        onClick={() => {
          activationHandler(offer.id, "expired");
        }}
      >
        Expirován
      </Button>
    ) : (
      <CustomLink
        dataTest="myOffers.showOfferButton"
        alignItems="center"
        display="flex"
        h="32px"
        w="fit-content"
        prefetch={false}
        variant={
          status === EOfferStatus.CANCELED
            ? "button_outline_gray"
            : "button_outline"
        }
        onClick={(e) => {
          e.preventDefault();
          navigateTo(
            "/muj-inzerat/[id]",
            { id: offer.id.toString() },
            { fresh: Date.now() },
          );
        }}
        to="/muj-inzerat/[id]"
        routeProps={{ id: offer.id.toString() }}
      >
        Zobrazit inzerát
      </CustomLink>
    );

  return (
    <CardLayout
      flex={1}
      topImage={
        <NextImage
          priority
          src={offer.photos[0].path || "/img/common/fotka_nenalezena.jpg"}
          alt={`Ilustrační obrázek ${calculateTitle(offer)}`}
          fill
          style={{
            transition: "all 0.2s",
            filter: status === EOfferStatus.ACTIVE ? undefined : "gray",
            WebkitFilter:
              status === EOfferStatus.ACTIVE ? undefined : "grayscale(1)",
          }}
        />
      }
      content={
        <>
          <Heading
            textAlign="center"
            as="h4"
            fontSize="1.125rem"
            lineHeight="1.25em"
          >
            {calculateTitle(offer)}
          </Heading>
          <Stack
            direction="row"
            justify="space-between"
            width="100%"
            p="15px 0"
          >
            <CustomLink
              dataTest="myOffers.editButton"
              to="/uprava-inzeratu/kontakt/[status]/[id]/[paidId]"
              display="inline-flex"
              alignItems="center"
              routeProps={
                paidTypeId
                  ? {
                      status,
                      id: offer.id.toString(),
                      paidId: paidTypeId.toString(),
                    }
                  : {
                      status,
                      id: offer.id.toString(),
                    }
              }
              trackEventPayload={{
                "dl.eCat": "inzerce",
                "dl.eAct": "click_upravit",
                "dl.eLab": "moje_inzeraty",
              }}
            >
              <IconEdit id="Edit" fillWith="ud-primary" boxSize="14px" />
              <Text
                variant="small_2"
                color="ud-primary"
                display="inline-block"
                ml="2"
              >
                Upravit
              </Text>
            </CustomLink>
            <CustomLink
              dataTest="myOffers.statisticsButton"
              to="/statistiky-inzeratu/[offerId]"
              display="inline-flex"
              alignItems="center"
              routeProps={{ offerId: offer.id.toString() }}
              trackEventPayload={{
                "dl.eCat": "inzerce",
                "dl.eAct": "click_statistiky",
                "dl.eLab": "moje_inzeraty",
              }}
            >
              <IconStats id="Stats" fillWith="ud-primary" boxSize="19px" />
              <Text
                variant="small_2"
                color="ud-primary"
                display="inline-block"
                ml="1.5"
              >
                Statistiky
              </Text>
            </CustomLink>
          </Stack>
          {resolveButton()}
        </>
      }
    />
  );
};
export default CardOffer;
